import type OpenAI from 'openai';
import type {
  FunctionTool,
  Response,
  ResponseCreateParamsBase,
  ResponseFormatTextConfig,
  ResponseFunctionToolCall,
  ResponseIncludable,
  ResponseInput,
  ResponseInputContent,
  ResponseInputItem,
  ResponseOutputItem,
  ResponseOutputMessage,
  ResponseReasoningItem,
  ResponseStreamEvent,
  Tool,
} from 'openai/resources/responses/responses';
import { ModelBehaviorError, UserError } from '../../errors';
import logger from '../../logger';
import type {
  Model,
  ModelMetadata,
  ModelRequest,
  ModelResponse,
  ModelRetryAdvice,
  ModelRetryAdviceRequest,
  ModelSettingsReasoningEffort,
  SerializedHandoff,
  SerializedHostedTool,
  SerializedOutputType,
  SerializedTool,
} from '../../model';
import { resetCurrentSpan, setCurrentSpan } from '../../tracing/context';
import {
  createGenerationSpan,
  withGenerationSpan,
} from '../../tracing/create-spans';
import { type GenerationSpanData, Span } from '../../tracing/spans';
import type { ResponseStreamEvent as LyraResponseStreamEvent } from '../../types/helpers';
import * as protocol from '../../types/protocol';
import { Usage } from '../../usage';
import {
  createLyraReplayPayload,
  getLyraReplayPayload,
  sanitizeLyraReplayProviderData,
  withLyraReplayPayload,
} from '../replay';
import { getOpenAIRetryAdvice } from './retry-advice';

type OpenAIResponsesCreateParams = ResponseCreateParamsBase & {
  stream: boolean;
  [key: string]: unknown;
};

type OpenAIResponsesClient = Pick<OpenAI, 'responses'>;

type StreamingState = {
  id: string;
  outputItems: Map<number, Record<string, unknown>>;
  outputItemIds: Map<string, Record<string, unknown>>;
  textParts: Map<string, string>;
  reasoningSummaries: Map<string, string>;
  functionArguments: Map<string, string>;
};

type ResponsesToolBatchProjection = {
  remainingToolCallIds: Set<string>;
  imageContent: ResponseInputContent[];
};

const OPENAI_PROVIDER_NAME = 'openai';
const DEFAULT_OPENAI_MAX_OUTPUT_TOKENS = 4096;

export class OpenAIResponsesModel implements Model {
  #client: OpenAIResponsesClient;
  #model: string;
  metadata?: ModelMetadata;

  constructor(
    client: OpenAIResponsesClient,
    model: string,
    metadata?: ModelMetadata,
  ) {
    this.#client = client;
    this.#model = model;
    this.metadata = metadata;
  }

  getRetryAdvice(args: ModelRetryAdviceRequest): ModelRetryAdvice | undefined {
    return getOpenAIRetryAdvice(args);
  }

  async getResponse(request: ModelRequest): Promise<ModelResponse> {
    const fetchResponse = async (
      span: Span<GenerationSpanData> | undefined,
    ) => {
      if (!span) return this.#fetchResponse(request, false);

      span.spanData.model = this.#model;
      span.spanData.model_config = {
        temperature: request.modelSettings.temperature,
        top_p: request.modelSettings.topP,
        reasoning_effort: request.modelSettings.reasoning?.effort,
      };
      const response = await this.#fetchResponse(request, false);
      if (request.tracing === true) {
        span.spanData.output = [
          sanitizeLyraReplayProviderData(
            response as unknown as Record<string, unknown>,
          ),
        ];
      }
      return response;
    };

    const response = request.tracing
      ? await withGenerationSpan(fetchResponse)
      : await fetchResponse(undefined);

    return responseToModelResponse(response);
  }

  async *getStreamedResponse(
    request: ModelRequest,
  ): AsyncIterable<LyraResponseStreamEvent> {
    const span = request.tracing ? createGenerationSpan() : undefined;
    const state: StreamingState = {
      id: 'openai-response',
      outputItems: new Map(),
      outputItemIds: new Map(),
      textParts: new Map(),
      reasoningSummaries: new Map(),
      functionArguments: new Map(),
    };
    let completed = false;

    try {
      if (span) {
        span.start();
        setCurrentSpan(span);
      }

      const stream = await this.#fetchResponse(request, true);
      for await (const event of stream) {
        const record = event as unknown as Record<string, unknown>;
        yield {
          type: 'model',
          event: record,
          providerData: { rawModelEventSource: 'openai_responses' },
        };

        for (const normalized of applyOpenAIResponseStreamEvent(
          state,
          record,
        )) {
          yield normalized;
          if (normalized.type === 'response_done') {
            completed = true;
          }
        }
      }

      if (!completed) {
        yield buildStreamResponseDoneEvent(state);
      }

      if (span && request.tracing === true) {
        span.spanData.output = [
          sanitizeLyraReplayProviderData({
            id: state.id,
            output: Array.from(state.outputItems.values()),
          }),
        ];
      }
    } catch (error) {
      if (span) {
        span.setError({
          message: 'Error streaming response',
          data: {
            error:
              request.tracing === true
                ? String(error)
                : error instanceof Error
                  ? error.name
                  : undefined,
          },
        });
      }
      throw error;
    } finally {
      if (span) {
        span.end();
        resetCurrentSpan();
      }
    }
  }

  async #fetchResponse(request: ModelRequest, stream: false): Promise<Response>;
  async #fetchResponse(
    request: ModelRequest,
    stream: true,
  ): Promise<AsyncIterable<ResponseStreamEvent>>;
  async #fetchResponse(
    request: ModelRequest,
    stream: boolean,
  ): Promise<Response | AsyncIterable<ResponseStreamEvent>> {
    const requestData = buildOpenAIResponsesRequest(
      this.#model,
      request,
      stream,
    );

    logger.debug(
      `Calling OpenAI Responses API${
        logger.dontLogModelData
          ? ''
          : `. Request data: ${JSON.stringify(
              sanitizeLyraReplayProviderData(requestData),
              null,
              2,
            )}`
      }`,
    );

    const response = (await this.#client.responses.create(
      requestData as any,
      request.signal ? { signal: request.signal } : undefined,
    )) as unknown;

    if (!stream) {
      logger.debug(
        `OpenAI Responses response received${
          logger.dontLogModelData
            ? ''
            : `: ${JSON.stringify(
                sanitizeLyraReplayProviderData(response),
                null,
                2,
              )}`
        }`,
      );
    }

    return response as Response | AsyncIterable<ResponseStreamEvent>;
  }
}

function buildOpenAIResponsesRequest(
  model: string,
  request: ModelRequest,
  stream: boolean,
): OpenAIResponsesCreateParams {
  const conversion = itemsToResponseInput(request);
  const providerData = { ...(request.modelSettings.providerData ?? {}) };
  const include = new Set<ResponseIncludable>([
    'reasoning.encrypted_content',
    ...extractProviderInclude(providerData),
  ]);
  delete providerData.include;

  const requestData: OpenAIResponsesCreateParams = {
    model,
    input: conversion.input,
    instructions: conversion.instructions || undefined,
    include: Array.from(include),
    max_output_tokens:
      request.modelSettings.maxTokens ?? DEFAULT_OPENAI_MAX_OUTPUT_TOKENS,
    parallel_tool_calls: request.modelSettings.parallelToolCalls,
    reasoning: getOpenAIReasoningSettings(request),
    store: request.modelSettings.store ?? false,
    stream,
    temperature: request.modelSettings.temperature,
    text: getOpenAITextConfig(request.outputType),
    tool_choice: getOpenAIToolChoice(request.modelSettings.toolChoice),
    tools: [
      ...(request.tools ?? []).map(toolToOpenAIResponses),
      ...(request.handoffs ?? []).map(handoffToOpenAIResponses),
    ],
    top_p: request.modelSettings.topP,
    truncation: request.modelSettings.truncation,
    ...providerData,
  };

  return requestData;
}

function extractProviderInclude(
  providerData: Record<string, unknown>,
): ResponseIncludable[] {
  const include = providerData.include;
  if (!Array.isArray(include)) {
    return [];
  }
  return include.filter(
    (item): item is ResponseIncludable => typeof item === 'string',
  );
}

function getOpenAIReasoningSettings(
  request: ModelRequest,
): OpenAIResponsesCreateParams['reasoning'] | undefined {
  const reasoning = request.modelSettings.reasoning;
  if (!reasoning) {
    return undefined;
  }
  const effort = normalizeOpenAIReasoningEffort(reasoning.effort);
  if (!effort && !reasoning.summary) {
    return undefined;
  }
  return {
    ...(effort ? { effort } : {}),
    ...(reasoning.summary
      ? { summary: reasoning.summary }
      : { summary: 'auto' }),
  } as OpenAIResponsesCreateParams['reasoning'];
}

function normalizeOpenAIReasoningEffort(
  effort: ModelSettingsReasoningEffort | undefined,
): 'low' | 'medium' | 'high' | undefined {
  if (effort === undefined || effort === null || effort === 'none') {
    return undefined;
  }
  if (effort === 'minimal') {
    return 'low';
  }
  if (effort === 'xhigh') {
    return 'high';
  }
  return effort;
}

function getOpenAITextConfig(
  outputType: SerializedOutputType,
): { format: ResponseFormatTextConfig } | undefined {
  if (outputType === 'text') {
    return undefined;
  }
  return {
    format: {
      type: 'json_schema',
      name: outputType.name,
      strict: outputType.strict,
      schema: outputType.schema,
    },
  };
}

function itemsToResponseInput(request: ModelRequest): {
  input: ResponseInput;
  instructions: string;
} {
  const input: ResponseInput = [];
  const instructions: string[] = [];
  let activeToolBatch: ResponsesToolBatchProjection | null = null;
  const flushSyntheticToolImages = () => {
    const batch = activeToolBatch as ResponsesToolBatchProjection | null;
    if (!batch || batch.imageContent.length === 0) {
      if (batch?.remainingToolCallIds.size === 0) {
        activeToolBatch = null;
      }
      return;
    }
    if (batch.remainingToolCallIds.size > 0) {
      return;
    }
    input.push({
      type: 'message',
      role: 'user',
      content: [
        {
          type: 'input_text',
          text: 'Images returned by tools in the previous tool batch.',
        },
        ...batch.imageContent,
      ],
    });
    activeToolBatch = null;
  };
  if (request.systemInstructions) {
    instructions.push(request.systemInstructions);
  }

  if (typeof request.input === 'string') {
    input.push({
      type: 'message',
      role: 'user',
      content: [{ type: 'input_text', text: request.input }],
    });
    return { input, instructions: instructions.join('\n\n') };
  }

  for (const item of request.input) {
    if ((item as { type?: unknown }).type === 'compaction') {
      throw new UserError(
        'Responses compaction items are not supported by the OpenAI Responses provider. Use Runner-level local compaction instead.',
      );
    }
    if ('role' in item) {
      flushSyntheticToolImages();
      if (item.role === 'system') {
        instructions.push(item.content);
      } else if (item.role === 'user') {
        input.push({
          type: 'message',
          role: 'user',
          content: userContentToResponseInput(item.content),
        });
      } else if (item.role === 'assistant') {
        input.push(assistantMessageToResponseItem(item));
      }
      continue;
    }

    if (item.type === 'reasoning') {
      flushSyntheticToolImages();
      const replay = getLyraReplayPayload(
        item.providerData as Record<string, unknown> | undefined,
        OPENAI_PROVIDER_NAME,
      );
      if (replay) {
        input.push(...(replay.blocks as unknown as ResponseInputItem[]));
        continue;
      }
      input.push(reasoningItemToResponseInput(item));
      continue;
    }

    if (item.type === 'function_call') {
      flushSyntheticToolImages();
      input.push({
        type: 'function_call',
        id: item.id,
        call_id: item.callId,
        name: item.name,
        arguments: item.arguments,
        status: item.status,
      });
      const batch: ResponsesToolBatchProjection = activeToolBatch ?? {
        remainingToolCallIds: new Set<string>(),
        imageContent: [],
      };
      batch.remainingToolCallIds.add(item.callId);
      activeToolBatch = batch;
      continue;
    }

    if (item.type === 'function_call_result') {
      const projection = projectFunctionCallOutputForResponses(
        item.output,
        item,
      );
      input.push({
        type: 'function_call_output',
        call_id: item.callId,
        output: projection.toolContent,
        status: item.status,
      });
      const batch = activeToolBatch as ResponsesToolBatchProjection | null;
      if (batch) {
        batch.remainingToolCallIds.delete(item.callId);
        batch.imageContent.push(...projection.imageContent);
        activeToolBatch = batch;
        flushSyntheticToolImages();
      } else if (projection.imageContent.length > 0) {
        input.push({
          type: 'message',
          role: 'user',
          content: [
            {
              type: 'input_text',
              text: 'Images returned by tools in the previous tool batch.',
            },
            ...projection.imageContent,
          ],
        });
      }
      continue;
    }

    if (item.type === 'hosted_tool_call') {
      flushSyntheticToolImages();
      throw new UserError(
        'Hosted tool calls are not supported by the OpenAI Responses provider v1.',
      );
    }

    if (item.type === 'unknown' && item.providerData) {
      flushSyntheticToolImages();
      input.push(item.providerData as unknown as ResponseInputItem);
    }
  }
  flushSyntheticToolImages();

  return { input, instructions: instructions.join('\n\n') };
}

function userContentToResponseInput(
  content: protocol.UserMessageItem['content'],
): ResponseInputContent[] {
  if (typeof content === 'string') {
    return [{ type: 'input_text', text: content }];
  }
  return content.map((part) => {
    if (part.type === 'input_text') {
      return { type: 'input_text', text: part.text };
    }
    if (part.type === 'input_image') {
      return inputImageToResponseInput(part);
    }
    throw new UserError('OpenAI Responses does not support this content part.');
  });
}

function inputImageToResponseInput(
  part: protocol.InputImage,
): ResponseInputContent {
  const detail = normalizeImageDetail(getOpenAIImageDetail(part));
  if (typeof part.image === 'object' && part.image?.id) {
    return { type: 'input_image', file_id: part.image.id, detail };
  }
  if (typeof part.image !== 'string' || part.image.trim().length === 0) {
    throw new UserError(
      'OpenAI Responses image inputs require a URL, data URL, base64 string, or file id.',
    );
  }
  const image = part.image.trim();
  return {
    type: 'input_image',
    detail,
    image_url:
      /^https?:\/\//i.test(image) || image.startsWith('data:')
        ? image
        : `data:image/jpeg;base64,${image}`,
  };
}

function getOpenAIImageDetail(part: protocol.InputImage): string | undefined {
  const providerData = part.providerData;
  const openai =
    providerData && typeof providerData.openai === 'object'
      ? (providerData.openai as Record<string, unknown>)
      : undefined;
  return typeof openai?.detail === 'string'
    ? openai.detail
    : typeof part.detail === 'string'
      ? part.detail
      : undefined;
}

function normalizeImageDetail(
  detail: string | undefined,
): 'low' | 'high' | 'auto' {
  return detail === 'low' || detail === 'high' || detail === 'auto'
    ? detail
    : 'auto';
}

function assistantMessageToResponseItem(
  item: protocol.AssistantMessageItem,
): ResponseOutputMessage {
  return {
    id: item.id ?? `msg_${Math.random().toString(36).slice(2)}`,
    type: 'message',
    role: 'assistant',
    status: item.status,
    content: item.content.map((part) => {
      if (part.type === 'output_text') {
        return {
          type: 'output_text',
          text: part.text,
          annotations: [],
        };
      }
      if (part.type === 'refusal') {
        return { type: 'refusal', refusal: part.refusal };
      }
      throw new UserError(
        'OpenAI Responses does not support assistant image content.',
      );
    }),
  };
}

function reasoningItemToResponseInput(
  item: protocol.ReasoningItem,
): ResponseReasoningItem {
  const summaryText =
    item.content.map((part) => part.text).join('') ||
    item.rawContent?.map((part) => part.text).join('') ||
    '';
  return {
    id: item.id ?? `rs_${Math.random().toString(36).slice(2)}`,
    type: 'reasoning',
    summary: summaryText ? [{ type: 'summary_text', text: summaryText }] : [],
    status: 'completed',
  };
}

function toolToOpenAIResponses(tool: SerializedTool): Tool {
  if (tool.type !== 'function') {
    throw new UserError(
      `Hosted tools are not supported by the OpenAI Responses provider v1. Got tool type: ${
        (tool as SerializedHostedTool).type
      }`,
    );
  }
  if (typeof tool.namespace === 'string' && tool.namespace.trim().length > 0) {
    throw new UserError(
      'Namespaced function tools are not supported by the OpenAI Responses provider v1.',
    );
  }
  return {
    type: 'function',
    name: tool.name,
    description: tool.description || null,
    parameters: tool.parameters,
    strict: tool.strict,
  } satisfies FunctionTool;
}

function handoffToOpenAIResponses(handoff: SerializedHandoff): Tool {
  return {
    type: 'function',
    name: handoff.toolName,
    description: handoff.toolDescription || null,
    parameters: handoff.inputJsonSchema,
    strict: handoff.strictJsonSchema,
  } satisfies FunctionTool;
}

function getOpenAIToolChoice(
  toolChoice: ModelRequest['modelSettings']['toolChoice'],
): OpenAIResponsesCreateParams['tool_choice'] | undefined {
  if (!toolChoice || toolChoice === 'auto') {
    return undefined;
  }
  if (toolChoice === 'none') {
    return 'none';
  }
  if (toolChoice === 'required') {
    return 'required';
  }
  return { type: 'function', name: toolChoice };
}

function responseToModelResponse(response: Response): ModelResponse {
  return {
    usage: new Usage(toUsageData(response.usage)),
    output: responseOutputToLyraOutput(response.output ?? [], response.id),
    responseId: response.id,
    providerData: sanitizeLyraReplayProviderData(
      response as unknown as Record<string, unknown>,
    ),
  };
}

function responseOutputToLyraOutput(
  outputItems: ResponseOutputItem[],
  responseId: string,
): protocol.OutputModelItem[] {
  const output: protocol.OutputModelItem[] = [];
  for (const item of outputItems) {
    const record = item as unknown as Record<string, unknown>;
    if (item.type === 'message') {
      output.push(responseMessageToLyraMessage(item));
      continue;
    }
    if (item.type === 'function_call') {
      output.push(responseFunctionCallToLyra(item, responseId));
      continue;
    }
    if (item.type === 'reasoning') {
      output.push(responseReasoningToLyra(item));
      continue;
    }
    output.push({
      id: getString(record.id) ?? responseId,
      type: 'unknown',
      providerData: record,
    });
  }
  return output;
}

function responseMessageToLyraMessage(
  item: ResponseOutputMessage,
): protocol.AssistantMessageItem {
  const content: protocol.AssistantContent[] = item.content.map((part) => {
    const record = part as unknown as Record<string, unknown>;
    if (part.type === 'output_text') {
      return {
        type: 'output_text',
        text: part.text,
        providerData: record,
      };
    }
    return {
      type: 'refusal',
      refusal: part.refusal,
      providerData: record,
    };
  });
  return {
    id: item.id,
    type: 'message',
    role: 'assistant',
    status: item.status,
    content,
    providerData: item as unknown as Record<string, unknown>,
  };
}

function responseFunctionCallToLyra(
  item: ResponseFunctionToolCall,
  responseId: string,
): protocol.FunctionCallItem {
  return {
    id: item.id ?? responseId,
    type: 'function_call',
    callId: item.call_id,
    name: item.name,
    arguments: item.arguments,
    status: item.status ?? 'completed',
    providerData: item as unknown as Record<string, unknown>,
  };
}

function responseReasoningToLyra(
  item: ResponseReasoningItem,
): protocol.ReasoningItem {
  const summaryText = item.summary.map((part) => part.text).join('');
  const { encrypted_content: _encryptedContent, ...visibleItem } =
    item as unknown as Record<string, unknown>;
  const replayBlock = {
    type: 'reasoning',
    id: item.id,
    summary: item.summary,
    status: item.status,
    ...(item.encrypted_content
      ? { encrypted_content: item.encrypted_content }
      : {}),
  };
  return {
    id: item.id,
    type: 'reasoning',
    content: summaryText ? [{ type: 'input_text', text: summaryText }] : [],
    rawContent: summaryText
      ? [{ type: 'reasoning_text', text: summaryText }]
      : undefined,
    providerData: withLyraReplayPayload(visibleItem, {
      ...createLyraReplayPayload({
        provider: OPENAI_PROVIDER_NAME,
        kind: 'reasoning',
        blocks: [replayBlock],
      }),
    }),
  };
}

function applyOpenAIResponseStreamEvent(
  state: StreamingState,
  event: Record<string, unknown>,
): LyraResponseStreamEvent[] {
  const type = event.type;
  if (type === 'response.created') {
    const response = asRecord(event.response);
    if (typeof response?.id === 'string') {
      state.id = response.id;
    }
    return [{ type: 'response_started', providerData: event }];
  }
  if (type === 'response.output_item.added') {
    const item = asRecord(event.item) ?? {};
    const outputIndex = Number(event.output_index ?? 0);
    state.outputItems.set(outputIndex, item);
    if (typeof item.id === 'string') {
      state.outputItemIds.set(item.id, item);
    }
    if (item.type === 'function_call') {
      const itemId = getString(item.id) ?? getString(event.item_id) ?? '';
      return [
        {
          type: 'output_part_started',
          partId: itemId,
          part: {
            type: 'function_call',
            callId: getString(item.call_id) ?? itemId,
            name: getString(item.name) ?? '',
          },
          providerData: event,
        },
      ];
    }
    if (item.type === 'reasoning') {
      const itemId = getString(item.id) ?? getString(event.item_id) ?? '';
      return [
        {
          type: 'output_part_started',
          partId: itemId,
          part: { type: 'reasoning' },
          providerData: event,
        },
      ];
    }
  }
  if (type === 'response.content_part.added') {
    const part = asRecord(event.part) ?? {};
    if (part.type === 'output_text' || part.type === 'refusal') {
      return [
        {
          type: 'output_part_started',
          partId: responseContentPartId(event),
          part: { type: 'output_text' },
          providerData: event,
        },
      ];
    }
  }
  if (type === 'response.output_text.delta') {
    const delta = getString(event.delta) ?? '';
    appendMapValue(state.textParts, responseContentPartId(event), delta);
    return delta
      ? [{ type: 'output_text_delta', delta, providerData: event }]
      : [];
  }
  if (
    type === 'response.reasoning_summary_text.delta' ||
    type === 'response.reasoning_summary.delta'
  ) {
    const delta = getString(event.delta) ?? '';
    appendMapValue(state.reasoningSummaries, responseItemId(event), delta);
    return delta
      ? [{ type: 'reasoning_delta', delta, providerData: event }]
      : [];
  }
  if (type === 'response.function_call_arguments.delta') {
    const delta = getString(event.delta) ?? '';
    const partId = responseItemId(event);
    appendMapValue(state.functionArguments, partId, delta);
    return delta
      ? [{ type: 'tool_input_delta', partId, delta, providerData: event }]
      : [];
  }
  if (
    type === 'response.content_part.done' ||
    type === 'response.output_item.done'
  ) {
    return [
      {
        type: 'output_part_done',
        partId:
          type === 'response.content_part.done'
            ? responseContentPartId(event)
            : responseItemId(event),
        providerData: event,
      },
    ];
  }
  if (type === 'response.completed') {
    const response = asRecord(event.response) as Response | undefined;
    if (response?.id) {
      state.id = response.id;
    }
    return [
      {
        type: 'response_done',
        response: {
          id: response?.id ?? state.id,
          usage: toUsageData(response?.usage),
          output: response
            ? responseOutputToLyraOutput(response.output ?? [], response.id)
            : [],
        },
      },
    ];
  }
  if (type === 'response.failed') {
    throw new ModelBehaviorError(
      `OpenAI Responses stream failed: ${JSON.stringify(event)}`,
    );
  }
  return [];
}

function buildStreamResponseDoneEvent(
  state: StreamingState,
): protocol.StreamEventResponseCompleted {
  const items = Array.from(
    state.outputItems.values(),
  ) as unknown as ResponseOutputItem[];
  return {
    type: 'response_done',
    response: {
      id: state.id,
      usage: toUsageData(undefined),
      output: responseOutputToLyraOutput(items, state.id),
    },
  };
}

function responseItemId(event: Record<string, unknown>): string {
  return getString(event.item_id) ?? '';
}

function responseContentPartId(event: Record<string, unknown>): string {
  const itemId = responseItemId(event);
  return `${itemId}:content:${String(event.content_index ?? 0)}`;
}

function appendMapValue(map: Map<string, string>, key: string, value: string) {
  if (!value) {
    return;
  }
  map.set(key, `${map.get(key) ?? ''}${value}`);
}

function toUsageData(usage: Response['usage'] | undefined): protocol.UsageData {
  const inputTokens = Number(usage?.input_tokens ?? 0);
  const outputTokens = Number(usage?.output_tokens ?? 0);
  const totalTokens = Number(usage?.total_tokens ?? inputTokens + outputTokens);
  return {
    inputTokens,
    outputTokens,
    totalTokens,
    inputTokensDetails: usage?.input_tokens_details
      ? { ...usage.input_tokens_details }
      : {},
    outputTokensDetails: usage?.output_tokens_details
      ? { ...usage.output_tokens_details }
      : {},
  };
}

function projectFunctionCallOutputForResponses(
  output: protocol.FunctionCallResultItem['output'],
  item: protocol.FunctionCallResultItem,
): { toolContent: string; imageContent: ResponseInputContent[] } {
  if (typeof output === 'string') {
    return { toolContent: output, imageContent: [] };
  }

  if (Array.isArray(output)) {
    const toolText: string[] = [];
    const imageContent: ResponseInputContent[] = [];
    for (const part of output) {
      if (part.type === 'input_text') {
        toolText.push(part.text);
        continue;
      }
      if (part.type === 'input_image') {
        const projection = projectInputImageToolOutputForResponses(part, item);
        toolText.push(projection.summary);
        imageContent.push(...projection.content);
        continue;
      }
      throw new UserError(
        `OpenAI Responses does not support this tool output content: ${JSON.stringify(part)}`,
      );
    }
    return {
      toolContent: toolText.join('\n'),
      imageContent,
    };
  }

  if (output.type === 'text') {
    return { toolContent: output.text, imageContent: [] };
  }

  if (output.type === 'image') {
    const normalized = legacyImageOutputToInputImage(output);
    const projection = projectInputImageToolOutputForResponses(
      normalized,
      item,
    );
    return {
      toolContent: projection.summary,
      imageContent: projection.content,
    };
  }

  throw new UserError(
    'Only text and image tool outputs are supported for OpenAI Responses. Got item: ' +
      JSON.stringify(output),
  );
}

function projectInputImageToolOutputForResponses(
  part: protocol.InputImage,
  item: protocol.FunctionCallResultItem,
): { summary: string; content: ResponseInputContent[] } {
  const metadata = getViewImageMetadata(part.providerData);
  const label = metadata
    ? `${item.callId}: ${metadata.path} ${metadata.mimeType}${metadata.width && metadata.height ? ` ${metadata.width}x${metadata.height}` : ''}`
    : `${item.callId}: image returned by ${item.name}`;
  const summary = metadata
    ? `[image returned by ${item.name}: ${metadata.path} ${metadata.mimeType}${metadata.width && metadata.height ? ` ${metadata.width}x${metadata.height}` : ''}${metadata.byteSize ? ` ${formatByteSize(metadata.byteSize)}` : ''}]`
    : `[image returned by ${item.name}]`;
  return {
    summary,
    content: [
      { type: 'input_text', text: label },
      inputImageToResponseInput(part),
    ],
  };
}

function legacyImageOutputToInputImage(
  output: protocol.ToolOutputImage,
): protocol.InputImage {
  const image = output.image;
  if (typeof image === 'string') {
    return {
      type: 'input_image',
      image,
      providerData: output.providerData,
    };
  }
  if (image && typeof image === 'object') {
    if ('fileId' in image && typeof image.fileId === 'string') {
      return {
        type: 'input_image',
        image: { id: image.fileId },
        providerData: output.providerData,
      };
    }
    if ('url' in image && typeof image.url === 'string') {
      return {
        type: 'input_image',
        image: image.url,
        providerData: output.providerData,
      };
    }
    if ('data' in image && typeof image.data === 'string') {
      const mediaType =
        typeof image.mediaType === 'string' ? image.mediaType : 'image/jpeg';
      return {
        type: 'input_image',
        image: image.data.startsWith('data:')
          ? image.data
          : `data:${mediaType};base64,${image.data}`,
        providerData: output.providerData,
      };
    }
  }
  throw new UserError(
    `OpenAI Responses image tool outputs require string image data, data objects, URLs, or file ids: ${JSON.stringify(output)}`,
  );
}

function getViewImageMetadata(value: unknown): {
  path: string;
  mimeType: string;
  byteSize?: number;
  width?: number;
  height?: number;
} | null {
  const record = asRecord(value);
  if (!record || record.kind !== 'view_image') {
    return null;
  }
  if (typeof record.path !== 'string' || typeof record.mimeType !== 'string') {
    return null;
  }
  return {
    path: record.path,
    mimeType: record.mimeType,
    byteSize: typeof record.byteSize === 'number' ? record.byteSize : undefined,
    width: typeof record.width === 'number' ? record.width : undefined,
    height: typeof record.height === 'number' ? record.height : undefined,
  };
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}
